/**
 * 本人のパスキーの Card。**顧客向けとオペレーター向けで同じものを使う**。
 *
 * 設計: docs/design/2026-09-23-passkey-authentication.md 9 節（段階 1）
 *
 * ## この Card が守っていること
 *
 * - **登録の直前にパスワードを入れ直させる。** サーバーはパスワードを照合して
 *   からでないと登録のチケットを出さない。画面はその入力欄を出すだけで、
 *   照合の結果（違う・試行が多すぎる）はサーバーの言葉をそのまま出す
 * - **断りの理由は Card の中に言葉で出す。** キャンセル・二重登録・上限・
 *   パスワード不一致・この環境で未有効、のどれも、押した場所のすぐ近くに出す
 * - **代行中は操作させない。** サーバーも断るが、押してから断られる形にしない。
 *   Card は出したまま、操作を置かずに理由を言葉で出す（メンバー管理と同じ流儀）
 * - **削除はセッションの終了を伴う**ことを、確かめるときに言葉で出す
 *
 * GraphQL の文書は顧客向けアプリの `passkeys.graphql` が正本で、生成物は共通の
 * パッケージに出る（codegen.ts）。オペレーター向けアプリもここから使う。
 */

import { useState, type ReactNode } from "react";
import { useMutation, useQuery } from "urql";
import {
  browserSupportsWebAuthn,
  startRegistration,
} from "@simplewebauthn/browser";

import { formatJstDateTime } from "../format";
import {
  FinishPasskeyRegistrationDocument,
  RemovePasskeyDocument,
  RenamePasskeyDocument,
  StartPasskeyRegistrationDocument,
  ViewerPasskeysDocument,
  type ViewerPasskeysQuery,
} from "../graphql/generated/graphql";
import { Button, Card, ErrorState, LoadingState, Tag } from "./primitives";

type PasskeyRow = ViewerPasskeysQuery["viewerPasskeys"][number];

/** 名前の上限。サーバーと同じ値（超えたら API が断るが、入力欄でも止める）。 */
const MAX_LABEL_LENGTH = 64;

const CARD_TITLE = "パスキー";

const CARD_NOTE =
  "パスキーは、この端末の指紋・顔・PIN などで本人を確かめる手段です。登録しても、ログインは今までどおりパスワードで行えます。";

/** 削除の確認で必ず出す文言。削除は「端末の切り離し」として扱う。 */
export const PASSKEY_REMOVE_WARNING =
  "削除すると、この利用者のログイン中のセッションもすべて終了します";

/** 代行中に出す文言。 */
export const PASSKEY_DELEGATED_MESSAGE = "代行中は登録できません";

const DEVICE_TYPE_LABEL: Record<PasskeyRow["deviceType"], string> = {
  MULTI_DEVICE: "同期済み",
  SINGLE_DEVICE: "この端末だけ",
};

/** 認証器の操作で起きた失敗を、利用者の言葉に直す。 */
function registrationFailureMessage(error: unknown): string {
  const name = (error as { name?: unknown }).name;
  const code = (error as { code?: unknown }).code;
  if (
    name === "InvalidStateError" ||
    code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED"
  ) {
    return "この端末のパスキーはすでに登録されています。";
  }
  if (name === "NotAllowedError" || name === "AbortError") {
    return "パスキーの登録を取り消しました。もう一度お試しください。";
  }
  return "パスキーを登録できませんでした。もう一度お試しください。";
}

function formatDate(value: string | null | undefined): string {
  const formatted = formatJstDateTime(value);
  return formatted === "" ? "—" : formatted;
}

export function PasskeysPanel({
  delegated,
  help,
}: {
  /** 代行中か。代行中は操作を置かず、理由を言葉で出す。 */
  delegated: boolean;
  /** 見出しの横に置くヘルプ(ヘルプの仕組みを持つアプリだけが渡す)。 */
  help?: ReactNode;
}) {
  if (delegated) {
    return (
      <Card title={CARD_TITLE} note={CARD_NOTE} extra={help}>
        <p className="vd-stat-note" style={{ margin: 0 }} role="note">
          {PASSKEY_DELEGATED_MESSAGE}
          。パスキーは本人のログインで登録・変更・削除してください。
        </p>
      </Card>
    );
  }
  return <PasskeysPanelBody help={help} />;
}

function PasskeysPanelBody({ help }: { help?: ReactNode }) {
  const [result, refetch] = useQuery({
    query: ViewerPasskeysDocument,
    requestPolicy: "cache-and-network" as const,
  });
  const [, startMutation] = useMutation(StartPasskeyRegistrationDocument);
  const [, finishMutation] = useMutation(FinishPasskeyRegistrationDocument);
  const [, renameMutation] = useMutation(RenamePasskeyDocument);
  const [, removeMutation] = useMutation(RemovePasskeyDocument);

  const [askingPassword, setAskingPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const supported = browserSupportsWebAuthn();

  function reset(): void {
    setErrors([]);
    setNotice(null);
  }

  async function register(): Promise<void> {
    reset();
    setBusy(true);
    try {
      const started = await startMutation({ input: { password } });
      const startPayload = started.data?.startPasskeyRegistration;
      if (started.error !== undefined || startPayload === undefined) {
        setErrors([
          started.error?.message ?? "パスキーを登録できませんでした。",
        ]);
        return;
      }
      if (
        startPayload.userErrors.length > 0 ||
        startPayload.ticket == null ||
        startPayload.optionsJson == null
      ) {
        setErrors(startPayload.userErrors.map((error) => error.message));
        return;
      }
      // パスワードはここから先で使わない。入力欄からも消す
      setPassword("");

      let response: unknown;
      try {
        response = await startRegistration({
          optionsJSON: JSON.parse(startPayload.optionsJson) as Parameters<
            typeof startRegistration
          >[0]["optionsJSON"],
        });
      } catch (error) {
        setErrors([registrationFailureMessage(error)]);
        return;
      }

      const finished = await finishMutation({
        input: {
          ticket: startPayload.ticket,
          responseJson: JSON.stringify(response),
        },
      });
      const finishPayload = finished.data?.finishPasskeyRegistration;
      if (finished.error !== undefined || finishPayload === undefined) {
        setErrors([
          finished.error?.message ?? "パスキーを登録できませんでした。",
        ]);
        return;
      }
      if (finishPayload.userErrors.length > 0) {
        setErrors(finishPayload.userErrors.map((error) => error.message));
        return;
      }
      setAskingPassword(false);
      setNotice(
        `パスキー「${finishPayload.passkey?.label ?? ""}」を登録しました。`,
      );
      refetch({ requestPolicy: "network-only" });
    } finally {
      setBusy(false);
    }
  }

  async function rename(passkey: PasskeyRow): Promise<void> {
    reset();
    setBusy(true);
    try {
      const renamed = await renameMutation({
        input: { id: passkey.id, label: renameValue },
      });
      const renamedPayload = renamed.data?.renamePasskey;
      if (renamed.error !== undefined || renamedPayload === undefined) {
        setErrors([renamed.error?.message ?? "名前を変えられませんでした。"]);
        return;
      }
      if (renamedPayload.userErrors.length > 0) {
        setErrors(renamedPayload.userErrors.map((error) => error.message));
        return;
      }
      setRenamingId(null);
      refetch({ requestPolicy: "network-only" });
    } finally {
      setBusy(false);
    }
  }

  async function remove(passkey: PasskeyRow): Promise<void> {
    reset();
    setBusy(true);
    try {
      const removed = await removeMutation({ input: { id: passkey.id } });
      const removedPayload = removed.data?.removePasskey;
      if (removed.error !== undefined || removedPayload === undefined) {
        setErrors([removed.error?.message ?? "削除できませんでした。"]);
        return;
      }
      if (removedPayload.userErrors.length > 0) {
        setErrors(removedPayload.userErrors.map((error) => error.message));
        return;
      }
      setConfirmingId(null);
      setNotice(
        `パスキー「${passkey.label}」を削除しました。ログイン中のセッションは終了したため、次の操作でログインし直しを求められることがあります。`,
      );
      refetch({ requestPolicy: "network-only" });
    } finally {
      setBusy(false);
    }
  }

  if (result.error !== undefined) {
    return (
      <ErrorState
        title="パスキーを読み込めませんでした"
        description={result.error.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }
  if (result.data === undefined) {
    return <LoadingState label="パスキーを読み込んでいます" />;
  }

  const passkeys = result.data.viewerPasskeys;

  return (
    <Card
      title={CARD_TITLE}
      note={CARD_NOTE}
      extra={
        <span>
          {passkeys.length === 0 ? null : (
            <Tag tone="info">{`${String(passkeys.length)} 本`}</Tag>
          )}
          {help}
        </span>
      }
    >
      <div className="vd-stack" data-testid="passkeys-panel">
        {errors.length === 0 ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span className="vd-state-title">操作できませんでした</span>
            {errors.map((message) => (
              <span key={message}>{message}</span>
            ))}
          </div>
        )}
        {notice === null ? null : (
          <p className="vd-stat-note" role="status" style={{ margin: 0 }}>
            {notice}
          </p>
        )}

        {passkeys.length === 0 ? (
          <p className="vd-stat-note" style={{ margin: 0 }}>
            まだパスキーを登録していません。
          </p>
        ) : (
          <div className="vd-table-scroll">
            <table className="vd-table">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>端末の種類</th>
                  <th>登録日</th>
                  <th>最終利用日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {passkeys.map((passkey) => (
                  <tr key={passkey.id} data-testid="passkey-row">
                    <td>
                      {renamingId === passkey.id ? (
                        <span className="vd-stack">
                          <input
                            aria-label={`${passkey.label} の新しい名前`}
                            value={renameValue}
                            maxLength={MAX_LABEL_LENGTH}
                            onChange={(event) => {
                              setRenameValue(event.target.value);
                            }}
                          />
                          <span>
                            <Button
                              variant="primary"
                              disabled={busy || renameValue.trim().length === 0}
                              onClick={() => {
                                void rename(passkey);
                              }}
                            >
                              保存
                            </Button>{" "}
                            <Button
                              disabled={busy}
                              onClick={() => {
                                setRenamingId(null);
                              }}
                            >
                              やめる
                            </Button>
                          </span>
                        </span>
                      ) : (
                        passkey.label
                      )}
                    </td>
                    <td>{DEVICE_TYPE_LABEL[passkey.deviceType]}</td>
                    <td>{formatDate(passkey.createdAt)}</td>
                    <td>{formatDate(passkey.lastUsedAt)}</td>
                    <td>
                      {confirmingId === passkey.id ? (
                        <span className="vd-stack">
                          <span>
                            {`「${passkey.label}」を削除します。${PASSKEY_REMOVE_WARNING}。よろしいですか。`}
                          </span>
                          <span>
                            <Button
                              variant="primary"
                              disabled={busy}
                              onClick={() => {
                                void remove(passkey);
                              }}
                            >
                              はい、削除する
                            </Button>{" "}
                            <Button
                              disabled={busy}
                              onClick={() => {
                                setConfirmingId(null);
                              }}
                            >
                              やめる
                            </Button>
                          </span>
                        </span>
                      ) : (
                        <span>
                          <Button
                            disabled={busy}
                            aria-label={`${passkey.label} の名前を変える`}
                            onClick={() => {
                              reset();
                              setConfirmingId(null);
                              setRenamingId(passkey.id);
                              setRenameValue(passkey.label);
                            }}
                          >
                            名前を変える
                          </Button>{" "}
                          <Button
                            disabled={busy}
                            aria-label={`${passkey.label} を削除`}
                            onClick={() => {
                              reset();
                              setRenamingId(null);
                              setConfirmingId(passkey.id);
                            }}
                          >
                            削除
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!supported ? (
          <p className="vd-stat-note" style={{ margin: 0 }}>
            このブラウザはパスキーに対応していないため、ここからは登録できません。
          </p>
        ) : askingPassword ? (
          <form
            className="vd-stack"
            onSubmit={(event) => {
              event.preventDefault();
              void register();
            }}
          >
            <label className="vd-stack">
              <span>確認のため、いまのパスワードを入力してください</span>
              <input
                type="password"
                name="current-password"
                autoComplete="current-password"
                value={password}
                disabled={busy}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </label>
            <span>
              <Button
                type="submit"
                variant="primary"
                disabled={busy || password.length === 0}
              >
                {busy ? "登録しています" : "続ける"}
              </Button>{" "}
              <Button
                disabled={busy}
                onClick={() => {
                  setAskingPassword(false);
                  setPassword("");
                  reset();
                }}
              >
                やめる
              </Button>
            </span>
          </form>
        ) : (
          <span>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                reset();
                setRenamingId(null);
                setConfirmingId(null);
                setAskingPassword(true);
              }}
            >
              パスキーを登録
            </Button>
          </span>
        )}
      </div>
    </Card>
  );
}
